import { useEffect } from 'react'
import { useGetEnginesId } from '@/types/origin-point-engine'
import { useEngineStore } from '../stores/use-engine-store'
import type { EngineDetailResponse } from '../types/engine'

/**
 * useEngine - 获取引擎详情并同步到 Store
 *
 * 职责：
 * 1. 根据 engineId 从 API 获取完整的引擎详情
 * 2. 自动将获取的数据同步到 useEngineStore
 * 3. 返回 engine 数据和加载状态
 *
 * 使用场景：
 * - 从 discover 进入新会话
 * - 从会话列表恢复已有会话
 * - 从编辑器预览会话
 */
export const useEngine = (engineId: string | undefined) => {
  const setCurrentEngine = useEngineStore((s) => s.engine.setCurrentEngine)

  const { data: engine, isLoading } = useGetEnginesId(
    engineId ?? '',
    undefined,
    {
      query: {
        enabled: !!engineId,
        staleTime: 10_000,
        retry: 1,
        select: (data: unknown) => {
          const res = data as unknown as EngineDetailResponse
          return res.data.engine
        },
      },
    }
  )

  // 当 engine 数据获取成功后，自动同步到 store
  useEffect(() => {
    if (engine) {
      setCurrentEngine({
        id: engine.id,
        title: engine.name || engine.title,
        description: engine.description,
        coverURL: engine.imageUrl,
        backgroundImageUrl: engine.backgroundImageUrl,
      })
    }
  }, [engine, setCurrentEngine])

  return { engine, isLoading }
}
